"use client"

import { useEffect, useId, useMemo, useState, type ReactNode } from "react"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { CalculatorBookingCta } from "@/components/tools/calculator-booking-cta"
import { buttonVariants } from "@/components/ui/button"
import { bmiCalculatorKeys as k } from "@/i18n/bmi-calculator-keys"
import { formatDecimal } from "@/lib/locale-number-format"
import { cn } from "@/lib/utils"
import {
  BMI_CATEGORIES,
  bmiCategoryFor,
  healthyWeightRangeForDisplay,
  roundBmi,
  type BmiCategory,
} from "./bmi"

/**
 * The interactive part of /tools/bmi-calculator.
 *
 * One centred column: the page intro, then the form, then the result panel,
 * which does not exist until Calculate has run. One component owns all three
 * because the form and the result share state; the `intro` slot is what keeps
 * the h1 in the client file, where the page's structure is meant to stay
 * readable.
 *
 * Deliberately framer-motion-free: the widget is always mounted, so any
 * animation here would have to clear the `<AnimatePresence>` + cacheComponents
 * trap (see `/animation-cache-trap`). CSS transitions cover what it needs.
 *
 * Nothing is submitted, stored or tracked: the arithmetic happens in the
 * browser and never leaves it. Note the page no longer SAYS so anywhere, since
 * the hero bullet that carried the promise was removed. Worth re-stating on
 * the surface if anyone asks where the data goes.
 */

/** Scale drawn under the result: 15 to 40, the readable part of the adult axis. */
const SCALE_MIN = 15
const SCALE_MAX = 40

/** Plausibility bounds. Outside these the inputs are almost certainly a typo. */
const MIN_HEIGHT_M = 0.9
const MAX_HEIGHT_M = 2.5
const MIN_WEIGHT_KG = 20
const MAX_WEIGHT_KG = 400

/**
 * Segment widths on the 15-to-40 axis, in BMI points. Obesity class III
 * (40 and above) has no segment: it starts where the axis ends, and the
 * marker clamps to the right edge.
 */
const SCALE_SEGMENTS: Array<{ id: BmiCategory; span: number }> = [
  /*
   * `id` is the BmiCategory union key, and its only job is to look up a colour
   * in CATEGORY_STYLES. Nothing renders it: the reader sees the localised
   * label from BMI_CATEGORIES. Hence the escape hatch on every line — the
   * rule reads `id: "<literal>"` as an identifier headed for the screen.
   */
  // no-raw-ids: colour lookup key, never rendered
  { id: "underweight", span: 18.5 - SCALE_MIN },
  // no-raw-ids: colour lookup key, never rendered
  { id: "healthy", span: 25 - 18.5 },
  // no-raw-ids: colour lookup key, never rendered
  { id: "overweight", span: 30 - 25 },
  // no-raw-ids: colour lookup key, never rendered
  { id: "obesity1", span: 35 - 30 },
  // no-raw-ids: colour lookup key, never rendered
  { id: "obesity2", span: SCALE_MAX - 35 },
]

/**
 * Tick labels under the bar, at their real position on the axis. The top one
 * reads "40+" because the axis stops there while obesity class III does not.
 */
const SCALE_TICKS: Array<{ value: number; label: string }> = [
  { value: 15, label: "15" },
  { value: 18.5, label: "18.5" },
  { value: 25, label: "25" },
  { value: 30, label: "30" },
  { value: 35, label: "35" },
  { value: SCALE_MAX, label: "40+" },
]

export const CATEGORY_STYLES: Record<
  BmiCategory,
  { bar: string; chip: string; accent: string }
> = {
  underweight: {
    bar: "bg-bright-blue-300",
    chip: "bg-bright-blue-100 text-bright-blue-900",
    accent: "text-bright-blue-700",
  },
  healthy: {
    bar: "bg-deep-green-400",
    chip: "bg-deep-green-100 text-deep-green-800",
    accent: "text-deep-green-600",
  },
  overweight: {
    bar: "bg-bright-green-400",
    chip: "bg-bright-green-100 text-bright-green-900",
    accent: "text-bright-green-700",
  },
  obesity1: {
    bar: "bg-terracotta-300",
    chip: "bg-terracotta-100 text-terracotta-900",
    accent: "text-terracotta-600",
  },
  obesity2: {
    bar: "bg-terracotta-400",
    chip: "bg-terracotta-100 text-terracotta-900",
    accent: "text-terracotta-700",
  },
  obesity3: {
    bar: "bg-terracotta-600",
    chip: "bg-terracotta-200 text-terracotta-950",
    accent: "text-terracotta-800",
  },
}

interface BmiResult {
  /** Rounded to the one decimal the reader sees — see `roundBmi`. */
  bmi: number
  /** Healthy weight span in kg, already rounded inward for display. */
  rangeLow: number
  rangeHigh: number
}

const inputClasses =
  "type-body-lg w-full rounded-lg border border-neutral-200 bg-white px-4 py-3 text-neutral-900 tabular-nums focus-visible:border-transparent focus-visible:ring-2 focus-visible:ring-aubergine-400 focus-visible:outline-none"

const unitSuffixClasses =
  "type-body-md pointer-events-none absolute inset-y-0 right-4 flex items-center text-neutral-500"

/**
 * NaN for a blank or unparseable field. `Number("")` is 0, which would read a
 * still-empty box as a real zero and show a result before the reader has
 * finished typing, so the blank case is checked explicitly.
 */
function parseField(value: string): number {
  const trimmed = value.trim().replace(",", ".")
  if (trimmed === "") return NaN
  return Number(trimmed)
}

export function BmiCalculatorWidget({ intro }: { intro?: ReactNode }) {
  const t = useTranslations("bmiCalculator")
  // The BMI is rendered here rather than coming from the catalog, so the
  // decimal separator has to be chosen explicitly: a German page printing
  // "22.9" beside prose that says "22,9" contradicts itself.
  const locale = useLocale()
  const fieldId = useId()

  /**
   * Flipped in an effect, and stamped onto the root as `data-hydrated`.
   *
   * Not decoration: the inputs are controlled, so anything that types into
   * them before React owns them is silently discarded, and an e2e test has no
   * other way to know when that moment has passed. The alternative in use
   * elsewhere in this suite is a blind `waitForTimeout(2000)`, which keeps
   * flaking on a loaded CI runner — three tests in
   * `e2e/bmi-calculator.spec.ts` did exactly that. One boolean and one effect
   * buys a deterministic signal instead of a guess. It renders nothing.
   */
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => {
    setHydrated(true)
  }, [])

  const [heightCm, setHeightCm] = useState("")
  const [weightKg, setWeightKg] = useState("")

  /**
   * The result only exists after Calculate, and any change to a field drops
   * it — a stale figure sitting beside edited inputs is the one thing a
   * side-by-side layout must not show. Unlike the BMR page there is no
   * post-calculation control here, so the rule is uniform: every field
   * invalidates.
   */
  const [result, setResult] = useState<BmiResult | null>(null)
  const [outOfRange, setOutOfRange] = useState(false)

  /** Every field setter goes through this. */
  function invalidate() {
    setResult(null)
    setOutOfRange(false)
  }

  const hasInput = heightCm.trim() !== "" || weightKg.trim() !== ""

  /**
   * Height in metres + weight in kg, or null while either field is blank.
   */
  const measurements = useMemo((): {
    heightM: number
    weightKgValue: number
  } | null => {
    const cm = parseField(heightCm)
    const kg = parseField(weightKg)
    if (!Number.isFinite(cm) || !Number.isFinite(kg)) return null
    return { heightM: cm / 100, weightKgValue: kg }
  }, [heightCm, weightKg])

  const inRange =
    measurements !== null &&
    measurements.heightM >= MIN_HEIGHT_M &&
    measurements.heightM <= MAX_HEIGHT_M &&
    measurements.weightKgValue >= MIN_WEIGHT_KG &&
    measurements.weightKgValue <= MAX_WEIGHT_KG

  /**
   * Plausibility is checked here rather than by disabling the button: a
   * disabled control with no explanation leaves the reader guessing, so the
   * button enables as soon as both fields are filled and an implausible
   * answer gets a message instead.
   *
   * The BMI is rounded first and classified from that same rounded value (see
   * `roundBmi`) — displaying one number and labelling another is the bug that
   * shape exists to prevent.
   */
  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!measurements) return
    if (!inRange) {
      setResult(null)
      setOutOfRange(true)
      return
    }
    const range = healthyWeightRangeForDisplay(measurements.heightM)
    setOutOfRange(false)
    setResult({
      bmi: roundBmi(
        measurements.weightKgValue /
          (measurements.heightM * measurements.heightM)
      ),
      rangeLow: range.low,
      rangeHigh: range.high,
    })
  }

  const category = result !== null ? bmiCategoryFor(result.bmi) : null
  const styles = category ? CATEGORY_STYLES[category] : null

  const rangeDisplay =
    result === null
      ? null
      : {
          // `bmi_calcHealthyRangeValue` declares {low}/{high} as ICU strings,
          // so hand next-intl strings rather than the raw numbers.
          low: String(result.rangeLow),
          high: String(result.rangeHigh),
          unit: t(k.calcUnitKg),
        }

  const markerPercent =
    result === null
      ? 0
      : Math.min(
          100,
          Math.max(
            0,
            ((result.bmi - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100
          )
        )

  function reset() {
    setHeightCm("")
    setWeightKg("")
    invalidate()
  }

  return (
    <div
      data-testid="bmi-calculator"
      data-hydrated={hydrated ? "true" : undefined}
      /* One centred column at the prose measure, so the calculator lines up
         with the sections below it rather than sitting in its own grid. */
      className="mx-auto w-full max-w-[760px]"
    >
      {/* ── Intro ── */}
      <div className="text-center">{intro}</div>

      {/* ── Form ── */}
      {/*
        `noValidate` is load-bearing. The two inputs carry min/max, so native
        constraint validation refuses to submit a 17 cm height and shows a
        browser bubble in the browser's own language — `handleSubmit` never
        runs, and `bmi_calcOutOfRange` can never appear. That was invisible
        while the panel always rendered a status line; with the panel gone
        until Calculate succeeds, it left the reader with no answer at all.
        The bounds stay on the inputs as spinner hints.
      */}
      <form onSubmit={handleSubmit} noValidate className="mt-10">
        <h2 className="type-title-lg text-center text-neutral-900">
          {t(k.calcTitle)}
        </h2>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor={`${fieldId}-height-cm`}
              className="type-label-md block text-neutral-700 normal-case"
            >
              {t(k.calcHeightLabel)}
            </label>
            <div className="relative mt-2">
              <input
                id={`${fieldId}-height-cm`}
                data-testid="bmi-height-cm"
                type="number"
                inputMode="decimal"
                min={90}
                max={250}
                step="0.5"
                autoComplete="off"
                placeholder="175"
                value={heightCm}
                onChange={(e) => {
                  setHeightCm(e.target.value)
                  invalidate()
                }}
                className={cn(inputClasses, "pr-14")}
              />
              <span className={unitSuffixClasses}>{t(k.calcUnitCm)}</span>
            </div>
          </div>
          <div>
            <label
              htmlFor={`${fieldId}-weight-kg`}
              className="type-label-md block text-neutral-700 normal-case"
            >
              {t(k.calcWeightLabel)}
            </label>
            <div className="relative mt-2">
              <input
                id={`${fieldId}-weight-kg`}
                data-testid="bmi-weight-kg"
                type="number"
                inputMode="decimal"
                min={20}
                max={400}
                step="0.1"
                autoComplete="off"
                placeholder="70"
                value={weightKg}
                onChange={(e) => {
                  setWeightKg(e.target.value)
                  invalidate()
                }}
                className={cn(inputClasses, "pr-14")}
              />
              <span className={unitSuffixClasses}>{t(k.calcUnitKg)}</span>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-4">
          {/*
            `buttonVariants` on a bare <button> rather than <Button>: the
            idiom this page's client already uses for the membership CTA, and
            it keeps `data-testid` off a custom component's prop type. The
            variant's `disabled:` classes cover the greyed state.
          */}
          <button
            type="submit"
            data-testid="bmi-submit"
            disabled={measurements === null}
            className={cn(buttonVariants({ variant: "primary" }), "flex-1")}
          >
            {t(k.calcSubmit)}
          </button>
          {hasInput && (
            <button
              type="button"
              onClick={reset}
              className="type-title-sm shrink-0 cursor-pointer text-neutral-500 underline underline-offset-4 transition-colors hover:text-neutral-900"
            >
              {t(k.calcReset)}
            </button>
          )}
        </div>

        {/*
          Plausibility feedback used to be the result panel's status line.
          With that panel gone until Calculate succeeds, the message belongs
          beside the control that produced it.
        */}
        {outOfRange && (
          <p
            data-testid="bmi-status"
            className="type-body-md text-terracotta-700 mt-4 text-center"
          >
            {t(k.calcOutOfRange)}
          </p>
        )}
      </form>

      {/* ── Result ── */}
      {/*
        The live region is mounted from the start so the panel is announced
        when it arrives; the panel itself only exists once Calculate has
        produced a figure, which is the whole point of the layout.
      */}
      <div aria-live="polite">
        {result !== null && category !== null && styles !== null && (
          <div
            data-testid="bmi-result"
            className="mt-8 rounded-[16px] bg-white p-6 md:p-8"
          >
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="type-label-md text-neutral-500 normal-case">
                  {t(k.calcResultLabel)}
                </p>
                <p
                  data-testid="bmi-value"
                  className={cn(
                    "type-display-md mt-1 tabular-nums",
                    styles.accent
                  )}
                >
                  {formatDecimal(result.bmi, 1, locale)}
                </p>
              </div>
              <span
                data-testid="bmi-category"
                className={cn(
                  "type-title-sm rounded-full px-4 py-1.5",
                  styles.chip
                )}
              >
                {t(BMI_CATEGORIES[category].labelKey)}
              </span>
            </div>

            {/* Scale */}
            <div className="mt-6">
              <div className="relative">
                {/*
                  Contiguous, no gap between segments. A gap would push every
                  boundary right of its true position while the marker is
                  placed on the ungapped percentage, so the marker would stop
                  landing on the band it names. Rounding lives on the clipping
                  container instead of the individual segments.
                */}
                <div className="flex h-2 w-full overflow-hidden rounded-full">
                  {SCALE_SEGMENTS.map((segment) => (
                    <div
                      key={segment.id}
                      className={cn("h-full", CATEGORY_STYLES[segment.id].bar)}
                      style={{
                        width: `${(segment.span / (SCALE_MAX - SCALE_MIN)) * 100}%`,
                      }}
                    />
                  ))}
                </div>
                <div
                  aria-hidden
                  className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-neutral-900 bg-white transition-[left] duration-300 ease-out"
                  style={{ left: `${markerPercent}%` }}
                />
              </div>
              {/*
                Ticks are absolutely positioned at their real place on the
                axis. The BMI cut-offs are not evenly spaced (18.5 sits at 14%
                of the 15-to-40 axis, 25 at 40%), so a flex justify-between row
                would print them at even intervals and mislabel the bands
                underneath.
              */}
              <div aria-hidden className="relative mt-3 h-4">
                {SCALE_TICKS.map((tick, index) => {
                  const isFirst = index === 0
                  const isLast = index === SCALE_TICKS.length - 1
                  return (
                    <span
                      key={tick.label}
                      className={cn(
                        "type-label-sm absolute top-0 text-neutral-400 normal-case tabular-nums",
                        isFirst && "left-0",
                        isLast && "right-0",
                        !isFirst && !isLast && "-translate-x-1/2"
                      )}
                      style={
                        isFirst || isLast
                          ? undefined
                          : {
                              left: `${((tick.value - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100}%`,
                            }
                      }
                    >
                      {tick.label}
                    </span>
                  )
                })}
              </div>
              <p className="type-body-sm mt-2 text-neutral-500">
                {t(k.calcScaleCaption)}
              </p>
            </div>

            {rangeDisplay && (
              <div className="mt-6 border-t border-neutral-200 pt-4">
                <p className="type-label-md text-neutral-500 normal-case">
                  {t(k.calcHealthyRangeLabel)}
                </p>
                <p
                  data-testid="bmi-healthy-range"
                  className="type-title-md mt-1 text-neutral-900 tabular-nums"
                >
                  {t(k.calcHealthyRangeValue, rangeDisplay)}
                </p>
              </div>
            )}

            <p className="type-body-md mt-4 text-neutral-600">
              {t(BMI_CATEGORIES[category].meaningKey)}
            </p>
            {/*
          Check-up hand-off. Inside the result panel and gated on `result`, so
          it is the reward for pressing Calculate rather than another CTA
          sitting on the page from the start. It books the market's blood
          check-up directly (CalculatorBookingCta) rather than sending the
          reader through the quiz first, and it does NOT claim the booking
          reads this result.
        */}
            <div className="bg-aubergine-50 mt-6 rounded-lg p-5">
              <h3 className="type-title-md text-neutral-900">
                {t(k.quizHeading)}
              </h3>
              <p className="type-body-md mt-2 text-neutral-600">
                {t(k.quizBody)}
              </p>
              <CalculatorBookingCta calculator="bmi">
                {t(k.quizCta)}
              </CalculatorBookingCta>
            </div>
          </div>
        )}
      </div>

      {/* The caveat is not part of the result: it applies to the tool, so it
          stays on the page whether or not anything has been calculated. */}
      <p className="type-body-sm mx-auto mt-8 max-w-[600px] text-center text-neutral-500">
        {t(k.calcDisclaimer)}
      </p>
    </div>
  )
}
